import clsx from 'clsx'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Group from 'components/utils/Group'
import useOffer from 'hooks/Offers/useOffer'
import { useRegionTimeFormat } from 'hooks/useRegionTimeFormat'
import useToggle from 'hooks/useToggle'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import { useCallback, useMemo } from 'react'
import * as React from 'react'
import styled from 'styled-components'
import GroupWithSeparator from 'tripPlanner/components/CleanCard/common/GroupWithSeparator'
import HoverOnlyRegenerateItemButton from 'tripPlanner/components/CleanCard/common/HoverOnlyRegenerateItemButton'
import { useTrip } from 'tripPlanner/hooks/api/trip'
import type { AccommodationItem } from 'tripPlanner/types/tripItem'
import {
  formatShortDateRange,
  getOfferImages,
  isLeBookmark,
} from 'tripPlanner/utils'
import { buildProductLink } from 'tripPlanner/utils/bookmark/restoreCart'
import { getItemPinBadge } from 'tripPlanner/utils/tripItemPin'
import ItemPin from '../CleanCard/common/ItemPin'
import { getItemPinCategory } from '../CleanCard/common/itemPinTypes'
import TripItemInlineNoteEdit from '../ItemCard/TripItemInlineNoteEdit'
import PlaceActionChips from '../PlaceActionChips'
import { startPlaceOfTripItem } from '../TripMaps/utils'
import ContainerBreakpoint from '../util/ContainerBreakpoint'
import Attachments from './common/Attachments'
import CornerControls from './common/CornerControls'
import { DetailCardAndPinLayout, DetailCardLayout } from './common/DetailCardAndPinLayout'
import ItemCardImage from './common/ItemCardImage'
import ItemCardImageCarousel from './common/ItemCardImageCarousel'
import ItemTitle from './common/ItemTitle'
import PrintOnlyAddressAndNotes from './common/PrintOnlyAddressAndNotes'
import type { DetailItemCardVariant } from './common/types'
import HotelPriceAndCTAs from './PriceAndCTAs/HotelPriceAndCTAs'
import HotelInclusionLabel from '../ItemCard/HotelInclusionLabel'

const StyledContainerBreakpoint = styled(ContainerBreakpoint)`
  height: 100%;
`
interface Props {
  item: AccommodationItem
  variant: DetailItemCardVariant
  tripNameLabel?: React.ReactNode
}

function DetailAccommodationCard({ item, variant }: Props) {
  const [inFocus, , setInFocusOn, setInFocusOff] = useToggle(false)
  const [showComment, , setShowCommentOn, setShowCommentOff] = useToggle(!!item.notes)
  const timeFormat = useRegionTimeFormat()
  const offerId = 'code' in item ? item.code : undefined
  const [offer] = useOffer<App.HotelOffer | App.VillaOffer | App.BedbankOffer>(offerId)
  const { data } = useTrip({ tripId: item.tripId })
  const hasInclusions = useMemo(() => data?.items?.some((tripItem) => tripItem.isHotelInclusion), [data?.items])

  const offerLink = isLeBookmark(item) ? buildProductLink(item, { offer }) : undefined
  const place = startPlaceOfTripItem(item)

  const itemImages = useMemo(() => getOfferImages(offer), [offer])

  const placeId = 'id' in item.place ? item.place.id : undefined

  const { itemNumber, itemColor } = useMemo(
    () => getItemPinBadge(item, variant, data),
    [item, variant, data],
  )

  const checkInText = useMemo(() => {
    if (!item.startTime) return undefined
    return isLeBookmark(item) ?
      `Check-in from ${item.startTime.format(timeFormat)}` :
      `Check-in at ${item.startTime.format(timeFormat)}`
  }, [item, timeFormat])

  const timeDescription = formatShortDateRange({ ...item, includeYear: false })

  const onRequestAddComment = useCallback(() => {
    setShowCommentOn()
    setInFocusOn()
  }, [setShowCommentOn, setInFocusOn])

  const onNotesChange = useCallback((notes: string | undefined) => {
    if (notes) {
      setShowCommentOn()
    } else {
      setShowCommentOff()
    }
  }, [setShowCommentOn, setShowCommentOff])

  const ImageComponent = useMemo(() => {
    if (itemImages.length > 1) {
      return (<>
        <StyledContainerBreakpoint min={640}>
          <ItemCardImageCarousel images={itemImages} className={clsx({ 'showComment': showComment })} />
        </StyledContainerBreakpoint>
        <StyledContainerBreakpoint max={640}>
          <ItemCardImage
            item={item}
            name={item.place.name}
            imgId={itemImages[0]?.id}
            imgSrc={itemImages[0]?.url}
            placeId={placeId}
            className={clsx({ 'showComment': showComment })}
          />
        </StyledContainerBreakpoint>
      </>)
    }

    return <ItemCardImage
      item={item}
      name={item.place.name}
      imgId={itemImages[0]?.id}
      imgSrc={itemImages[0]?.url}
      placeId={placeId}
      className={clsx({ 'showComment': showComment })}
    />
  }, [itemImages, item, placeId, showComment])

  return (
    <DetailCardAndPinLayout data-testid="detail-card-accommodation">
      <DetailCardAndPinLayout.Pin>
        <ItemPin
          variant={variant}
          category={getItemPinCategory(item.type)}
          itemNumber={itemNumber}
          dayColor={itemColor}
        />
      </DetailCardAndPinLayout.Pin>
      <DetailCardAndPinLayout.CardContainer>
        <DetailCardLayout.Image>
          <HoverOnlyRegenerateItemButton item={item} />
          {ImageComponent}
        </DetailCardLayout.Image>
        <DetailCardLayout.Content>
          <CornerControls item={item} offerLink={offerLink} onRequestAddComment={onRequestAddComment} />
          <VerticalSpacer gap={12}>
            <Group direction="vertical" gap={4}>
              <ItemTitle>{item.place.name}</ItemTitle>
              <GroupWithSeparator direction="horizontal" verticalAlign="center" gap={4} desktopGap={8} wrap="wrap">
                {checkInText && variant === 'itinerary' && (
                  <BodyTextBlock
                    variant="small"
                    colour="neutral-two"
                  >
                    {checkInText}
                  </BodyTextBlock>
                )}
                {timeDescription && (
                  <BodyTextBlock
                    variant="small"
                    colour="neutral-two"
                  >
                    {timeDescription}
                  </BodyTextBlock>
                )}
                {hasInclusions && (
                  <HotelInclusionLabel hotelName={item.place.name} isHotelItem />
                )}
              </GroupWithSeparator>
            </Group>
            <Attachments item={item} />
            <StyledContainerBreakpoint min={640}>
              {offer && (
                <HotelPriceAndCTAs
                  item={item}
                  offer={offer}
                  variant={variant}
                />
              )}
            </StyledContainerBreakpoint>
            <PrintOnlyAddressAndNotes item={item} />
            {!!place && <PlaceActionChips
              place={place}
            />}
          </VerticalSpacer>
        </DetailCardLayout.Content>
        {showComment && (
          <DetailCardLayout.Comment>
            <TripItemInlineNoteEdit
              item={item}
              inputAlwaysVisible
              inFocus={inFocus}
              onFocus={setInFocusOn}
              onBlur={setInFocusOff}
              onNotesChange={onNotesChange}
            />
          </DetailCardLayout.Comment>
        )}
      </DetailCardAndPinLayout.CardContainer>
    </DetailCardAndPinLayout>
  )
}

export default DetailAccommodationCard
